import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import appConfig from "../../config/appConfig";
import { ToastBottomEnd } from "./Toast";
import {
    toastAddErrorMessageConfig,
    toastAddSuccessMessageConfig,
} from "../../config/toastConfig";
import { verifyPermission } from "../../helpers/permission";
import { connect } from "react-redux";
import { verifyTokenExpire } from "../../middleware/verifyToken";
import { useTranslation } from "react-i18next";

const UnfoundedModal = (props) => {
    const { t, ready, i18n } = useTranslation();
    const currentLang = i18n.language || "fr";

    const defaultData = {
        unfounded_reason_id: "",
        unfounded_comment: "",
        solution_communicated: "",
    };

    const defaultError = {
        unfounded_reason_id: [],
        unfounded_comment: [],
        solution_communicated: [],
    };

    const [data, setData] = useState(defaultData);
    const [error, setError] = useState(defaultError);
    const [startRequest, setStartRequest] = useState(false);
    const [reasons, setReasons] = useState([]);
    const [reasonsLoading, setReasonsLoading] = useState(false);

    // Fonction de chargement des motifs
    const fetchReasons = useCallback(async () => {
        setReasonsLoading(true);
        try {
            const response = await axios.get(`${appConfig.apiDomaine}/unfounded-reasons`);
            const responseData = response.data;
            if (Array.isArray(responseData)) {
                setReasons(responseData);
            } else if (responseData && Array.isArray(responseData.data)) {
                setReasons(responseData.data);
            } else {
                setReasons([]);
            }
        } catch (err) {
            console.error("Erreur chargement des motifs non fondés:", err);
            setReasons([]);
        } finally {
            setReasonsLoading(false);
        }
    }, []);

    // Charger les motifs au montage ET ré-essayer au clic / ouverture du modal
    useEffect(() => {
        fetchReasons();
    }, [fetchReasons, props.getId]);

    // Écouter l'événement d'ouverture du modal Bootstrap pour recharger la liste à chaque ouverture
    useEffect(() => {
        if (window.$) {
            window.$("#exampleModal").on("show.bs.modal", () => {
                fetchReasons();
            });
        }
        return () => {
            if (window.$) {
                window.$("#exampleModal").off("show.bs.modal");
            }
        };
    }, [fetchReasons]);

    // Initialiser les champs si un traitement actif existe
    useEffect(() => {
        if (props.activeTreatment) {
            setData({
                unfounded_reason_id: props.activeTreatment.unfounded_reason_id || "",
                unfounded_comment: props.activeTreatment.unfounded_comment || props.activeTreatment.unfounded_reason || "",
                solution_communicated: props.activeTreatment.solution_communicated || "",
            });
        }
    }, [props.activeTreatment]);

    // Formatage du libellé traduisible (fr/en)
    const getReasonLabel = (reason) => {
        if (!reason || !reason.libelle) return "";
        if (typeof reason.libelle === "string") return reason.libelle;
        return reason.libelle[currentLang] || reason.libelle["fr"] || reason.libelle["en"] || "";
    };

    const onChangeReasonId = (e) => {
        setData({
            ...data,
            unfounded_reason_id: e.target.value,
        });
    };

    const onChangeComment = (e) => {
        setData({
            ...data,
            unfounded_comment: e.target.value,
        });
    };

    const onChangeSolution = (e) => {
        setData({
            ...data,
            solution_communicated: e.target.value,
        });
    };

    const onSubmit = (e) => {
        e.preventDefault();
        setStartRequest(true);

        if (verifyTokenExpire()) {
            const isPilot = verifyPermission(
                props.userPermissions,
                "unfounded-claim-awaiting-assignment"
            );

            const endpoint = isPilot
                ? `${appConfig.apiDomaine}/claim-awaiting-assignment/${props.getId}/unfounded`
                : `${appConfig.apiDomaine}/claim-assignment-staff/${props.getId}/unfounded`;

            const redirectUrl = isPilot
                ? "/process/claim-assign"
                : "/process/claim-assign/to-staff";

            axios
                .put(endpoint, data)
                .then(() => {
                    setStartRequest(false);
                    ToastBottomEnd.fire(toastAddSuccessMessageConfig());
                    if (window.$) {
                        window.$("#exampleModal").modal("hide");
                    }
                    window.location.href = redirectUrl;
                })
                .catch((err) => {
                    setStartRequest(false);
                    if (err.response && err.response.data && err.response.data.error) {
                        setError({ ...defaultError, ...err.response.data.error });
                    }
                    ToastBottomEnd.fire(toastAddErrorMessageConfig());
                });
        }
    };

    return ready ? (
        <div>
            <div
                className="modal fade"
                id="exampleModal"
                tabIndex="-1"
                role="dialog"
                aria-labelledby="exampleModalLabel"
                aria-hidden="true"
            >
                <div className="modal-dialog" role="document">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5 className="modal-title" id="exampleModalLabel">
                                {t("Réclamation non Fondée")}
                            </h5>
                            <button
                                type="button"
                                className="close"
                                data-dismiss="modal"
                                aria-label="Close"
                            >
                                <span aria-hidden="true">&times;</span>
                            </button>
                        </div>
                        <div className="modal-body">
                            {/* 1. SÉLECTION DU MOTIF */}
                            <div
                                className={
                                    error.unfounded_reason_id && error.unfounded_reason_id.length
                                        ? "form-group validated"
                                        : "form-group"
                                }
                            >
                                <label htmlFor="unfounded_reason_id">
                                    {t("Motif")} <span style={{ color: "red" }}>*</span>{" "}
                                    <i
                                        className="flaticon2-information text-info"
                                        style={{ cursor: "pointer", marginLeft: "4px" }}
                                        title={t("Veuillez sélectionner le motif pré-configuré justifiant le rejet de la réclamation")}
                                    />
                                </label>
                                <select
                                    id="unfounded_reason_id"
                                    className={
                                        error.unfounded_reason_id && error.unfounded_reason_id.length
                                            ? "form-control is-invalid"
                                            : "form-control"
                                    }
                                    value={data.unfounded_reason_id}
                                    onChange={onChangeReasonId}
                                    disabled={reasonsLoading}
                                >
                                    <option value="">
                                        {reasonsLoading
                                            ? t("Chargement des motifs...")
                                            : t("Sélectionner un motif")}
                                    </option>
                                    {reasons.map((reason) => (
                                        <option key={reason.id} value={reason.id}>
                                            {getReasonLabel(reason)}
                                        </option>
                                    ))}
                                </select>
                                {error.unfounded_reason_id && error.unfounded_reason_id.length ? (
                                    error.unfounded_reason_id.map((err, index) => (
                                        <div key={index} className="invalid-feedback">
                                            {err}
                                        </div>
                                    ))
                                ) : null}
                            </div>

                            {/* 2. COMMENTAIRE EXPLICATIF */}
                            <div
                                className={
                                    error.unfounded_comment && error.unfounded_comment.length
                                        ? "form-group validated"
                                        : "form-group"
                                }
                            >
                                <label htmlFor="unfounded_comment">
                                    {t("Commentaire")} <span style={{ color: "red" }}>*</span>
                                </label>
                                <textarea
                                    id="unfounded_comment"
                                    className={
                                        error.unfounded_comment && error.unfounded_comment.length
                                            ? "form-control is-invalid"
                                            : "form-control"
                                    }
                                    placeholder={t("Veuillez saisir un commentaire détaillé explicatif")}
                                    cols="62"
                                    rows="5"
                                    value={data.unfounded_comment}
                                    onChange={onChangeComment}
                                />
                                {error.unfounded_comment && error.unfounded_comment.length ? (
                                    error.unfounded_comment.map((err, index) => (
                                        <div key={index} className="invalid-feedback">
                                            {err}
                                        </div>
                                    ))
                                ) : null}
                            </div>

                            {/* 3. SOLUTION COMMUNIQUÉE (PILOTE) */}
                            {verifyPermission(
                                props.userPermissions,
                                "unfounded-claim-awaiting-assignment"
                            ) ? (
                                <div
                                    className={
                                        error.solution_communicated && error.solution_communicated.length
                                            ? "form-group validated"
                                            : "form-group"
                                    }
                                >
                                    <label htmlFor="solution_communicated">
                                        {t("Solution")} <span style={{ color: "red" }}>*</span>
                                    </label>
                                    <textarea
                                        id="solution_communicated"
                                        className={
                                            error.solution_communicated && error.solution_communicated.length
                                                ? "form-control is-invalid"
                                                : "form-control"
                                        }
                                        placeholder={t("Message à communiquer au client")}
                                        cols="30"
                                        rows="5"
                                        value={data.solution_communicated}
                                        onChange={onChangeSolution}
                                    />
                                    {error.solution_communicated && error.solution_communicated.length ? (
                                        error.solution_communicated.map((err, index) => (
                                            <div key={index} className="invalid-feedback">
                                                {err}
                                            </div>
                                        ))
                                    ) : null}
                                </div>
                            ) : null}
                        </div>

                        {/* PIED DU MODAL */}
                        <div className="modal-footer">
                            {!startRequest ? (
                                <button
                                    type="submit"
                                    onClick={onSubmit}
                                    className="btn btn-primary"
                                >
                                    {t("Enregistrer")}
                                </button>
                            ) : (
                                <button
                                    className="btn btn-primary kt-spinner kt-spinner--left kt-spinner--md kt-spinner--light"
                                    type="button"
                                    disabled
                                >
                                    {t("Chargement")}...
                                </button>
                            )}
                            <button
                                type="button"
                                className="btn btn-secondary"
                                data-dismiss="modal"
                            >
                                {t("Quitter")}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    ) : null;
};

const mapStateToProps = (state) => ({
    userPermissions: state.user.user.permissions,
});

export default connect(mapStateToProps)(UnfoundedModal);